import { useMemo, useCallback, useRef } from 'react';
import { sortByQueue } from '../utils/queueSort';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * useWaitingTimePredictor — Intelligent Waiting Time Prediction Engine
 *
 * Calculates per-patient predicted waiting time using:
 *   1. Historical Average — weighted moving avg of doctor's completed consultations
 *   2. Ongoing Visit Duration — if current visit exceeds average → inflate future estimates
 *   3. Patients Ahead — position in queue × predicted consultation time
 *   4. Queue Movement Speed — recent throughput rate (completions/hour)
 *
 * Prediction Guardrails:
 *   - Weighted moving average (recent visits weigh more)
 *   - Capped delta: max ±30% adjustment per recalculation cycle
 *   - Confidence buffer: +12% to avoid unrealistic optimism
 *   - Smoothing: exponential moving average on predictions to avoid jitter
 *   - Min/Max bounds: 2 min floor, 60 min cap per consultation slot
 *
 * Updates automatically when:
 *   - dailyVisits change (WebSocket push → visitStore patch → useMemo recalc)
 *   - consultationStats are fetched/refreshed
 *
 * No polling. No interval. Pure derived state.
 * ═══════════════════════════════════════════════════════════════════════
 */

// ── Constants ────────────────────────────────────────────────────────
const DEFAULT_CONSULTATION_SEC = 5 * 60;       // 5 minutes fallback
const MIN_CONSULTATION_SEC = 2 * 60;           // 2 min floor
const MAX_CONSULTATION_SEC = 60 * 60;          // 60 min cap
const CONFIDENCE_BUFFER = 0.12;                // +12% optimism guard
const MAX_DELTA_RATIO = 0.30;                  // max ±30% per cycle
const ONGOING_OVERSHOOT_WEIGHT = 0.35;         // how much ongoing overshoot inflates estimates
const SPEED_ADJUSTMENT_WEIGHT = 0.25;          // how much recent speed adjusts prediction

/**
 * Parse an ISO datetime string to a Date object.
 * Handles both "Z" and "+05:30" suffixes.
 */
function parseISO(str) {
  if (!str) return null;
  try {
    return new Date(str);
  } catch {
    return null;
  }
}

/**
 * Compute weighted moving average of consultation durations.
 * Recent durations have linearly increasing weight (1, 2, 3, ..., n).
 * Uses last `maxSamples` entries.
 */
function weightedMovingAvg(durations, maxSamples = 10) {
  if (!durations.length) return DEFAULT_CONSULTATION_SEC;

  const recent = durations.slice(-maxSamples);
  let wSum = 0;
  let wTotal = 0;
  for (let i = 0; i < recent.length; i++) {
    const weight = i + 1; // linearly increasing
    wSum += recent[i] * weight;
    wTotal += weight;
  }
  return wSum / wTotal;
}

/**
 * Clamp a value between min and max.
 */
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * Apply capped delta adjustment.
 * New prediction can only differ from baseline by ±MAX_DELTA_RATIO.
 */
function cappedDelta(baseline, adjusted) {
  const maxDelta = baseline * MAX_DELTA_RATIO;
  const delta = adjusted - baseline;
  const clampedDelta = clamp(delta, -maxDelta, maxDelta);
  return baseline + clampedDelta;
}


// ═══════════════════════════════════════════════════════════════════════
// Main Hook
// ═══════════════════════════════════════════════════════════════════════

/**
 * @param {Array} dailyVisits — all visits for today (from visitStore)
 * @param {Object|null} consultationStats — backend response: { doctors: { [docId]: { avg_duration_sec, ... } } }
 * @returns {Object} { getDoctorPrediction, getPatientWaitTime, doctorAvgMap }
 */
export default function useWaitingTimePredictor(dailyVisits, consultationStats) {
  // Cache previous predictions for smoothing
  const prevPredictions = useRef({});

  // ── Step 1: Compute per-doctor stats from client-side data ────────
  const doctorAnalysis = useMemo(() => {
    const doctors = {};

    (dailyVisits || []).forEach((v) => {
      const docId = v.doctor_member_id || v.visit_doctor_id;
      if (!docId) return;

      if (!doctors[docId]) {
        doctors[docId] = {
          doctor_name: v.doctor_name || 'Unknown',
          completedDurations: [],         // seconds
          queueVisits: [],                // visits in QUEUE, sorted by queue_number
          inProgressVisit: null,          // current ongoing visit (if any)
          completedCount: 0,
          lastCompletedAt: null,
          recentCompletionTimes: [],      // timestamps of recent completions (for speed calc)
        };
      }

      const doc = doctors[docId];

      if (v.status === 'COMPLETED') {
        doc.completedCount++;
        const started = parseISO(v.started_at);
        const completed = parseISO(v.completed_at);
        if (started && completed) {
          const dur = Math.max(0, (completed - started) / 1000);
          if (dur >= MIN_CONSULTATION_SEC && dur <= MAX_CONSULTATION_SEC) {
            doc.completedDurations.push(dur);
          }
          doc.recentCompletionTimes.push(completed.getTime());
          if (!doc.lastCompletedAt || completed.getTime() > doc.lastCompletedAt) {
            doc.lastCompletedAt = completed.getTime();
          }
        }
      } else if (v.status === 'IN_PROGRESS') {
        // Take the first IN_PROGRESS visit (usually only one per doctor)
        if (!doc.inProgressVisit) {
          doc.inProgressVisit = v;
        }
      } else if (v.status === 'QUEUE') {
        doc.queueVisits.push(v);
      }
    });

    // Sort queue visits: interleave priority patients with walk-ins
    Object.values(doctors).forEach((doc) => {
      doc.queueVisits = sortByQueue(doc.queueVisits);
    });

    return doctors;
  }, [dailyVisits]);

  // ── Step 2: Merge backend stats with client-side analysis ─────────
  const doctorAvgMap = useMemo(() => {
    const map = {};
    const backendDoctors = consultationStats?.doctors || {};

    // Collect all doctor IDs
    const allDocIds = new Set([
      ...Object.keys(doctorAnalysis),
      ...Object.keys(backendDoctors),
    ]);

    allDocIds.forEach((docId) => {
      const client = doctorAnalysis[docId];
      const backend = backendDoctors[docId];

      // Prefer client-side durations (real-time), fall back to backend
      let baseAvg;
      if (client?.completedDurations?.length >= 2) {
        baseAvg = weightedMovingAvg(client.completedDurations);
      } else if (backend?.avg_duration_sec) {
        baseAvg = backend.avg_duration_sec;
      } else if (client?.completedDurations?.length === 1) {
        // One sample — blend with default
        baseAvg = (client.completedDurations[0] + DEFAULT_CONSULTATION_SEC) / 2;
      } else {
        baseAvg = DEFAULT_CONSULTATION_SEC;
      }

      // ── Ongoing visit adjustment ────────────────────────────
      let adjusted = baseAvg;
      if (client?.inProgressVisit) {
        const started = parseISO(client.inProgressVisit.started_at);
        if (started) {
          const elapsed = (Date.now() - started.getTime()) / 1000;
          if (elapsed > baseAvg) {
            // Ongoing visit is exceeding average → inflate future estimates
            const overshoot = elapsed - baseAvg;
            adjusted = baseAvg + (overshoot * ONGOING_OVERSHOOT_WEIGHT);
          }
        }
      }

      // ── Queue movement speed adjustment ────────────────────
      if (client?.recentCompletionTimes?.length >= 2) {
        const times = [...client.recentCompletionTimes].sort((a, b) => a - b);
        // Take last 5 intervals
        const intervals = [];
        for (let i = Math.max(0, times.length - 6); i < times.length - 1; i++) {
          intervals.push((times[i + 1] - times[i]) / 1000);
        }
        if (intervals.length > 0) {
          const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
          // If queue is moving faster than estimated → reduce
          // If slower → increase
          const speedDelta = (avgInterval - adjusted) * SPEED_ADJUSTMENT_WEIGHT;
          adjusted = adjusted + speedDelta;
        }
      }

      // ── Apply capped delta (max ±30% from base) ───────────
      adjusted = cappedDelta(baseAvg, adjusted);

      // ── Add confidence buffer (+12%) ───────────────────────
      adjusted = adjusted * (1 + CONFIDENCE_BUFFER);

      // ── Clamp to sane bounds ───────────────────────────────
      adjusted = clamp(adjusted, MIN_CONSULTATION_SEC, MAX_CONSULTATION_SEC);

      // ── Smoothing against previous prediction ──────────────
      const prev = prevPredictions.current[docId];
      if (prev != null) {
        // Exponential smoothing: 70% new, 30% old
        adjusted = adjusted * 0.7 + prev * 0.3;
      }
      prevPredictions.current[docId] = adjusted;

      map[docId] = {
        doctor_name: client?.doctor_name || backend?.doctor_name || 'Unknown',
        avgConsultationSec: Math.round(adjusted),
        sampleCount: client?.completedDurations?.length || backend?.sample_count || 0,
        queueLength: client?.queueVisits?.length || 0,
        hasOngoing: !!client?.inProgressVisit,
        completedToday: client?.completedCount || 0,
      };
    });

    return map;
  }, [doctorAnalysis, consultationStats]);

  // ── Step 3: Per-patient waiting time calculator ────────────────────
  const getPatientWaitTime = useCallback(
    /**
     * @param {string} visitId
     * @returns {{ waitMinutes: number, patientsAhead: number, confidence: string } | null}
     */
    (visitId) => {
      // Find the visit and its doctor
      const visit = (dailyVisits || []).find(
        (v) => (v.visit_id || v.id) === visitId
      );
      if (!visit || visit.status !== 'QUEUE') return null;

      const docId = visit.doctor_member_id || visit.visit_doctor_id;
      if (!docId) return null;

      const docData = doctorAnalysis[docId];
      const avgData = doctorAvgMap[docId];
      if (!docData || !avgData) return null;

      // Count patients ahead in queue (using priority-aware sorted position)
      const myIndex = docData.queueVisits.findIndex(
        (q) => (q.visit_id || q.id) === visitId
      );
      const ahead = myIndex >= 0 ? myIndex : docData.queueVisits.length;

      // Add 1 if there's an ongoing consultation (they need to finish first)
      const ongoingSlots = docData.inProgressVisit ? 1 : 0;

      // If there's an ongoing visit, calculate remaining time for it
      let ongoingRemaining = 0;
      if (docData.inProgressVisit) {
        const started = parseISO(docData.inProgressVisit.started_at);
        if (started) {
          const elapsed = (Date.now() - started.getTime()) / 1000;
          ongoingRemaining = Math.max(0, avgData.avgConsultationSec - elapsed);
        } else {
          // No started_at available — assume half the avg remaining
          ongoingRemaining = avgData.avgConsultationSec * 0.5;
        }
      }

      // Total wait = remaining of ongoing + (patients ahead × avg)
      const totalWaitSec = ongoingRemaining + (ahead * avgData.avgConsultationSec);
      const waitMinutes = Math.max(1, Math.round(totalWaitSec / 60));

      // Confidence level based on sample count
      let confidence;
      if (avgData.sampleCount >= 5) confidence = 'high';
      else if (avgData.sampleCount >= 2) confidence = 'medium';
      else confidence = 'low';

      return {
        waitMinutes,
        patientsAhead: ahead + ongoingSlots,
        confidence,
        avgConsultationMin: Math.round(avgData.avgConsultationSec / 60),
      };
    },
    [dailyVisits, doctorAnalysis, doctorAvgMap]
  );

  // ── Step 4: Doctor-level prediction summary ───────────────────────
  const getDoctorPrediction = useCallback(
    /**
     * @param {string} doctorId
     * @returns {{ avgMinutes: number, queueWaitMinutes: number, queueLength: number, confidence: string } | null}
     */
    (doctorId) => {
      const avg = doctorAvgMap[doctorId];
      if (!avg) return null;

      const doc = doctorAnalysis[doctorId];
      const queueLen = doc?.queueVisits?.length || 0;

      // Total queue wait for the last person
      let totalSec = 0;
      if (doc?.inProgressVisit) {
        const started = parseISO(doc.inProgressVisit.started_at);
        if (started) {
          const elapsed = (Date.now() - started.getTime()) / 1000;
          totalSec += Math.max(0, avg.avgConsultationSec - elapsed);
        } else {
          totalSec += avg.avgConsultationSec * 0.5;
        }
      }
      totalSec += queueLen * avg.avgConsultationSec;

      let confidence;
      if (avg.sampleCount >= 5) confidence = 'high';
      else if (avg.sampleCount >= 2) confidence = 'medium';
      else confidence = 'low';

      return {
        avgMinutes: Math.round(avg.avgConsultationSec / 60),
        queueWaitMinutes: Math.max(0, Math.round(totalSec / 60)),
        queueLength: queueLen,
        hasOngoing: avg.hasOngoing,
        completedToday: avg.completedToday,
        confidence,
      };
    },
    [doctorAvgMap, doctorAnalysis]
  );

  return {
    getPatientWaitTime,
    getDoctorPrediction,
    doctorAvgMap,
  };
}

/**
 * Format minutes to human-readable string.
 * @param {number} minutes
 * @returns {string} e.g. "~5 min", "~1 hr 15 min"
 */
export function formatWaitTime(minutes) {
  if (minutes == null || minutes <= 0) return '--';
  if (minutes < 2) return '~1 min';
  if (minutes < 60) return `~${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `~${hrs} hr`;
  return `~${hrs} hr ${mins} min`;
}

/**
 * Confidence level color map.
 */
export function confidenceColor(confidence, isDark) {
  switch (confidence) {
    case 'high':
      return isDark ? '#4ade80' : '#16a34a';
    case 'medium':
      return isDark ? '#fbbf24' : '#d97706';
    case 'low':
    default:
      return isDark ? '#9ca3af' : '#6b7280';
  }
}
